// EmailTemplateDialog — диалог создания/редактирования шаблона письма (V15 «Почта» W7, ТЗ §3.8).
// POST /api/email/templates {name, subject, contentType, body} (201) / PUT {id} (200);
// дубль названия → 409 → Alert. Палитра переменных §3.3 (8 {{...}}-плейсхолдеров) — вставка
// в body. Превью POST /api/email/templates/preview {templateId, invoiceId} — рендер как
// ТЕКСТ (textContent, dangerouslySetInnerHTML ЗАПРЕЩЁН — ТЗ §3.8/§11, XSS-поверхность).

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { emailApi, type EmailContentType, type EmailTemplate } from '@/api/email';
import { invoicesApi } from '@/api/invoices';
import { formatInvoiceNumber } from '@/lib/format';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Палитра переменных (фиксированный реестр §3.3; ТЗ: добавление — MINOR). */
const TEMPLATE_VARIABLES: readonly string[] = [
  'invoice_number',
  'invoice_sum',
  'invoice_currency',
  'deal_title',
  'client_name',
  'invoice_date',
  'due_date',
  'link',
];

/** Типы контента письма (HTML | PLAIN; multipart — V16+, OQ-4). */
const CONTENT_TYPE_OPTIONS: { value: EmailContentType; labelKey: string }[] = [
  { value: 'HTML', labelKey: 'email.templates.contentType.HTML' },
  { value: 'PLAIN', labelKey: 'email.templates.contentType.PLAIN' },
];

/** Границы валидации шаблона (M-7: name ≤120, subject ≤255, body ≤10000 — DDL/CHECK V40). */
const MAX_NAME_LENGTH = 120;
const MAX_SUBJECT_LENGTH = 255;
const MAX_BODY_LENGTH = 10000;

/** Суффикс выбора счёта для превью (последние счета; канон списков счетов). */
const INVOICES_OPTIONS_SIZE = 100;

interface EmailTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Редактируемый шаблон (null — создание нового; системные read-only в списке). */
  template?: EmailTemplate | null;
}

/** Диалог шаблона письма: форма + палитра переменных + превью (без отправки). */
export function EmailTemplateDialog({ open, onClose, template = null }: EmailTemplateDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [contentType, setContentType] = useState<EmailContentType>('HTML');
  const [body, setBody] = useState('');
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  // Сброс формы под шаблон при каждом открытии (канон InvoiceEditDialog).
  useEffect(() => {
    if (!open) return;
    setName(template?.name ?? '');
    setSubject(template?.subject ?? '');
    setContentType(template?.contentType ?? 'HTML');
    setBody(template?.body ?? '');
    setInvoiceId(null);
    setPreview(null);
  }, [open, template]);

  /** Справочник счетов для превью (RBAC счёта проверяет backend на preview, §3.5). */
  const invoicesQuery = useQuery({
    queryKey: ['invoices', 'options'],
    queryFn: () => invoicesApi.list({ page: 0, size: INVOICES_OPTIONS_SIZE }),
    enabled: open,
    retry: false,
  });

  const canSubmit =
    name.trim() !== '' &&
    subject.trim() !== '' &&
    body.trim() !== '' &&
    name.trim().length <= MAX_NAME_LENGTH &&
    subject.trim().length <= MAX_SUBJECT_LENGTH &&
    body.trim().length <= MAX_BODY_LENGTH;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), subject: subject.trim(), contentType, body };
      return template ? emailApi.updateTemplate(template.id, payload) : emailApi.createTemplate(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email', 'templates'] });
      onClose();
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => {
      if (!template) throw new Error('Превью доступно только для сохранённого шаблона');
      if (!invoiceId) throw new Error('Выберите счёт для превью');
      return emailApi.previewTemplate({ templateId: template.id, invoiceId });
    },
    onSuccess: (data) => setPreview({ subject: data.subject, body: data.body }),
  });

  /** Вставка переменной в body по позиции курсора (KISS: нет позиции — конец). */
  const insertVariable = (variable: string): void => {
    const token = `{{${variable}}}`;
    const el = bodyRef.current;
    if (el) {
      const start = el.selectionStart ?? body.length;
      const end = el.selectionEnd ?? body.length;
      setBody(body.slice(0, start) + token + body.slice(end));
      return;
    }
    setBody((prev) => prev + token);
  };

  const isPending = saveMutation.isPending || previewMutation.isPending;
  const saveError = saveMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(saveMutation.error, t('email.templates.saveError'))}</Alert>
  ) : null;
  const previewError = previewMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(previewMutation.error, t('email.templates.previewError'))}</Alert>
  ) : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('email.templates.dialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {saveError}
          <TextField
            label={t('email.templates.name')}
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ 'aria-label': t('email.templates.name') }}
          />
          <TextField
            label={t('email.templates.subject')}
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            inputProps={{ 'aria-label': t('email.templates.subject') }}
          />
          <TextField
            select
            label={t('email.templates.contentType')}
            value={contentType}
            onChange={(e) => setContentType(e.target.value as EmailContentType)}
            inputProps={{ 'aria-label': t('email.templates.contentType') }}
          >
            {CONTENT_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="caption">{t('email.templates.variables')}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {TEMPLATE_VARIABLES.map((variable) => (
                <Chip
                  key={variable}
                  size="small"
                  label={`{{${variable}}}`}
                  aria-label={variable}
                  onClick={() => insertVariable(variable)}
                />
              ))}
            </Stack>
          </Box>

          <TextField
            label={t('email.templates.body')}
            fullWidth
            multiline
            minRows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            inputRef={bodyRef}
            inputProps={{ 'aria-label': t('email.templates.body') }}
          />

          {/* Превью: выбор счёта → рендер как текст (dangerouslySetInnerHTML запрещён, ТЗ §11). */}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              label={t('email.templates.invoice')}
              value={invoiceId ?? ''}
              onChange={(e) => setInvoiceId(e.target.value || null)}
              inputProps={{ 'aria-label': t('email.templates.invoice') }}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="">
                <em>{t('email.templates.needInvoiceHint')}</em>
              </MenuItem>
              {(invoicesQuery.data?.content ?? []).map((invoice) => (
                <MenuItem key={invoice.id} value={invoice.id}>
                  {formatInvoiceNumber(invoice.invoiceNumber, invoice.invoiceDate)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              onClick={() => previewMutation.mutate()}
              disabled={isPending || !template || !invoiceId}
            >
              {t('email.templates.preview')}
            </Button>
          </Stack>

          {previewError}
          {preview ? (
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1">{preview.subject}</Typography>
              {/* textContent-рендер: HTML-теги отображаются как текст (канон U-33/§3.8). */}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {preview.body}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={isPending || !canSubmit}>
          {t('email.templates.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
